import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mui/material';
import { anchorRef, bindTrigger } from 'material-ui-popup-state';

import {
  TableContainer,
  Button,
  TableHeader,
  SearchForm,
  SearchFormProps,
  AddIcon,
  IconButton,
  FilterListIcon,
} from '@occc/ui-design-system';
import { useAppLayoutContextConsumer, Page } from '@occc-app/design-system';
import { useChainId } from '@occc-app/Chain/hooks';
import { useGetProductGroups } from '@occc-app/dataAccessLayer/productGroup/queries';
import { routeCreator } from '@occc-app/routes/ProductGroup/utils';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { ProductGroupsTable } from './components/ProductGroupsTable';
import { breadcrumbLinks } from './constants';
import { useProductGroupFilter, ProductGroupFilter } from './components/ProductGroupFilter';
import { ProductGroupFilterFormValues } from './components/ProductGroupFilter/types';

const PAGE_TITLE = 'Product Groups';

export const ProductGroupListPage = () => {
  const chainId = useChainId();
  useAppLayoutContextConsumer(breadcrumbLinks);

  const isUserPermittedToCreateProductGroup = useIsUserPermittedTo(Feature.PRODUCT_GROUP_CREATE);
  const isUserPermittedToEditProductGroup = useIsUserPermittedTo(Feature.PRODUCT_GROUP_EDIT);
  const isUserPermittedToDeleteProductGroup = useIsUserPermittedTo(Feature.PRODUCT_GROUP_DELETE);

  const [search, setSearch] = useSearchParams();
  const [paginationInfo, onPageChange] = usePaginationParams();
  const {
    identifierFilter,
    setIdentifierFilter,
    tarEligibleFilter,
    setTarEligibleFilter,
    productGroupPopupState,
    productGroupPopoverProps,
  } = useProductGroupFilter();

  const { loading, error, data, paginationProps } = useGetProductGroups({
    filter: {
      search,
      isDeleted: false,
      chainIdEquals: chainId,
      keyEquals: identifierFilter === 'all' ? undefined : identifierFilter,
      userSelectable: tarEligibleFilter ? true : undefined,
    },
    paginationInfo,
    onPageChange,
  });

  const tableActions = useMemo(() => {
    const onSearchFormSubmit: SearchFormProps['onSubmit'] = ({ search }) => {
      setSearch(search);
    };

    return (
      <Box display='flex' justifyContent='end' alignItems='center' width='100%'>
        <SearchForm onSubmit={onSearchFormSubmit} initialValues={{ search }} />
        <IconButton
          {...bindTrigger(productGroupPopupState)}
          ref={anchorRef(productGroupPopupState)}
          sx={{ ml: 1 }}
        >
          <FilterListIcon fontSize='inherit' />
        </IconButton>
      </Box>
    );
  }, [search, setSearch, productGroupPopupState]);

  const productGroupFilterValues = {
    identifier: identifierFilter,
    isTarEligible: tarEligibleFilter,
  };
  const setProductGroupFilterValues = (values: ProductGroupFilterFormValues) => {
    if (values.identifier !== identifierFilter) {
      setIdentifierFilter(values.identifier);
    }

    if (values.isTarEligible !== tarEligibleFilter) {
      setTarEligibleFilter(values.isTarEligible);
    }
  };

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      actions={
        isUserPermittedToCreateProductGroup && (
          <Button
            color='secondary'
            variant='contained'
            component={Link}
            startIcon={<AddIcon />}
            to={routeCreator.create()}
            role='button'
          >
            create new product group
          </Button>
        )
      }
      pageHeaderProps={{
        title: PAGE_TITLE,
      }}
    >
      <TableContainer>
        <TableHeader title={PAGE_TITLE} actions={tableActions} />
        <ProductGroupsTable
          productGroups={data}
          loading={loading}
          error={error}
          paginationProps={paginationProps}
          isUserPermittedToEditProductGroup={isUserPermittedToEditProductGroup}
          isUserPermittedToDeleteProductGroup={isUserPermittedToDeleteProductGroup}
        />
      </TableContainer>
      <ProductGroupFilter
        filter={productGroupFilterValues}
        setFilter={setProductGroupFilterValues}
        popoverProps={productGroupPopoverProps}
      />
    </Page>
  );
};
